import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../../context/AuthContext';
import supabase from '../../supabase/supabase';
import Button from '../../components/ui/Button';
import SafeIcon from '../../common/SafeIcon';
import { FiUser, FiMail, FiLock, FiCopy, FiCheckCircle, FiStar, FiAward, FiSend, FiLifeBuoy, FiGlobe, FiAlertCircle, FiChevronDown, FiCheck, FiX } from 'react-icons/fi';

const SettingsCard = ({ title, description, children, className = '' }) => (
  <div className={`p-6 rounded-2xl bg-zinc-900/40 backdrop-blur-md border border-orange-500/10 shadow-sm flex flex-col ${className}`}>
    <div className="mb-6">
      <h3 className="text-lg font-semibold text-amber-50 mb-1">{title}</h3>
      {description && <p className="text-sm text-zinc-400">{description}</p>}
    </div>
    <div className="flex-1 flex flex-col">
      {children}
    </div>
  </div>
);

const Settings = () => {
  const { user, handleCheckout } = useAuth();
  const fileInputRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();

  // Profile State
  const [profileLoading, setProfileLoading] = useState(true);
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileMessage, setProfileMessage] = useState({ type: '', text: '' });
  const [billingMessage, setBillingMessage] = useState({ type: '', text: '' });

  const [fullName, setFullName] = useState('');
  const [publicId, setPublicId] = useState('');
  const [avatarUrl, setAvatarUrl] = useState('');
  const [referralCode, setReferralCode] = useState('');
  const [totalReferred, setTotalReferred] = useState(0);
  const [subscription, setSubscription] = useState(null);

  const [isUploadingAvatar, setIsUploadingAvatar] = useState(false);
  const [isBillingLoading, setIsBillingLoading] = useState(false);

  // Other UI States
  const [copiedRef, setCopiedRef] = useState(false);
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [feedback, setFeedback] = useState('');
  const [isSubmittingFeedback, setIsSubmittingFeedback] = useState(false);
  const [showFeatures, setShowFeatures] = useState(false);

  // Check if email is verified
  const isEmailVerified = !!user?.email_confirmed_at;

  // Handle Payment Redirect Status
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const status = params.get('status');

    if (status === 'done') {
      setBillingMessage({ type: 'success', text: 'Payment successful! Your premium features are now active.' });
      navigate(location.pathname, { replace: true });
      setTimeout(() => setBillingMessage({ type: '', text: '' }), 8000);
    } else if (status === 'canceled') {
      setBillingMessage({ type: 'error', text: 'Checkout was canceled. You have not been charged.' });
      navigate(location.pathname, { replace: true });
      setTimeout(() => setBillingMessage({ type: '', text: '' }), 8000);
    }
  }, [location.search, location.pathname, navigate]);

  // Fetch Profile Data on Mount
  useEffect(() => {
    if (!user?.id) return;

    const fetchProfile = async () => {
      try {
        const { data, error } = await supabase
          .from('profiles')
          .select('full_name, public_id, avatar_url, referral_code, total_referred')
          .eq('id', user.id)
          .single();

        if (error) throw error;

        if (data) {
          setFullName(data.full_name || '');
          setPublicId(data.public_id || '');
          setAvatarUrl(data.avatar_url || '');
          setReferralCode(data.referral_code || '');
          setTotalReferred(data.total_referred || 0);
        }

        // Fetch Dodo Payments subscription details
        const { data: subData } = await supabase
          .from('subscriptions')
          .select('*')
          .eq('user_id', user.id)
          .order('updated_at', { ascending: false })
          .limit(1)
          .maybeSingle();

        if (subData) {
          setSubscription(subData);
        }

      } catch (err) {
        console.error('Error fetching profile:', err);
      } finally {
        setProfileLoading(false);
      }
    };

    fetchProfile();
  }, [user?.id]); // Depend on user ID to prevent re-fetching on token refresh

  // Save Profile Data
  const handleSaveProfile = async () => {
    setProfileSaving(true);
    setProfileMessage({ type: '', text: '' });

    try {
      const { error } = await supabase
        .from('profiles')
        .update({
          full_name: fullName,
          public_id: publicId
        })
        .eq('id', user.id);

      if (error) {
        if (error.code === '23505') {
          throw new Error('This Public ID is already taken. Please choose another.');
        }
        throw error;
      }

      setProfileMessage({ type: 'success', text: 'Profile updated successfully!' });
      setTimeout(() => setProfileMessage({ type: '', text: '' }), 3000);
    } catch (err) {
      setProfileMessage({ type: 'error', text: err.message || 'Failed to update profile.' });
    } finally {
      setProfileSaving(false);
    }
  };

  // Upload Avatar
  const handleAvatarUpload = async (event) => {
    try {
      const file = event.target.files[0];
      if (!file) return;

      setIsUploadingAvatar(true);
      setProfileMessage({ type: '', text: '' });

      const fileExt = file.name.split('.').pop();
      const fileName = `avatar-${Date.now()}.${fileExt}`;
      const filePath = `${user.id}/${fileName}`;

      // Upload to Storage
      const { error: uploadError } = await supabase.storage
        .from('avatars')
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      // Get Public URL
      const { data: { publicUrl } } = supabase.storage
        .from('avatars')
        .getPublicUrl(filePath);

      // Update Profile
      const { error: updateError } = await supabase
        .from('profiles')
        .update({ avatar_url: publicUrl })
        .eq('id', user.id);

      if (updateError) throw updateError;

      setAvatarUrl(publicUrl);
      setProfileMessage({ type: 'success', text: 'Avatar updated successfully!' });
      setTimeout(() => setProfileMessage({ type: '', text: '' }), 3000);

    } catch (error) {
      setProfileMessage({ type: 'error', text: error.message || 'Failed to upload avatar.' });
    } finally {
      setIsUploadingAvatar(false);
    }
  };

  // Handle Billing Portal Redirect
  const handleManageBilling = async () => {
    setIsBillingLoading(true);
    setBillingMessage({ type: '', text: '' });

    try {
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();

        if (sessionError || !session?.access_token) {
          throw new Error('You must be logged in to manage billing.');
        }

        const { data, error } = await supabase.functions.invoke('dodo-billings', {
          headers: {
            Authorization: `Bearer ${session.access_token}`
          }
        });

      if (error) throw error;

      if (data?.success && data?.portal_url) {
        // Attempt to open in a new tab
        const newWindow = window.open(data.portal_url, '_blank');
        // Fallback to same tab if popup blocker prevents the new tab
        if (!newWindow) {
          window.location.href = data.portal_url;
        }
      } else {
        throw new Error(data?.error || 'Could not retrieve billing portal URL.');
      }
    } catch (err) {
      console.error('Billing portal error:', err);
      setBillingMessage({ type: 'error', text: 'Failed to access billing portal. Please try again.' });
    } finally {
      setIsBillingLoading(false);
    }
  };

  const refLink = `${window.location.origin}/ref/${referralCode || 'your-code'}`;

  const handleCopyRef = () => {
    navigator.clipboard.writeText(refLink);
    setCopiedRef(true);
    setTimeout(() => setCopiedRef(false), 2000);
  };

  const handleFeedbackSubmit = () => {
    if (!rating && !feedback) return;
    setIsSubmittingFeedback(true);
    setTimeout(() => {
      setIsSubmittingFeedback(false);
      setRating(0);
      setFeedback('');
    }, 1000);
  };

  const inputStyles = "w-full bg-[#0a0500]/50 border border-orange-500/20 rounded-xl px-4 py-3 text-zinc-200 placeholder-zinc-600 focus:border-orange-500/50 focus:ring-1 focus:ring-orange-500/50 transition-all outline-none";

  const normalizeSubscriptionStatus = (status) => {
    return (status || '').toLowerCase();
  };

  const formatBillingDate = (date) => {
    if (!date) return '';
    return new Date(date).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const subscriptionStatus = normalizeSubscriptionStatus(subscription?.status);
  const subscriptionPeriodEnd = formatBillingDate(subscription?.current_period_end);

  const isSubscriptionCancelled =
    subscriptionStatus === 'cancelled' ||
    subscriptionStatus === 'canceled' ||
    !!subscription?.cancelled_at ||
    subscription?.cancel_at_next_billing_date === true;

  const isSubscriptionActive =
    subscriptionStatus === 'active' ||
    subscriptionStatus === 'trialing' ||
    subscriptionStatus === 'on_trial';

  const isSubscriptionPastDue =
    subscriptionStatus === 'past_due' ||
    subscriptionStatus === 'unpaid';

  const isSubscriptionInactive =
    subscriptionStatus === 'expired' ||
    subscriptionStatus === 'failed' ||
    subscriptionStatus === 'paused';

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="max-w-4xl mx-auto pb-12 space-y-8"
    >
      {/* Profile Setup */}
      <SettingsCard
        title="Profile Setup"
        description="Manage your personal information and public identity."
      >
        {profileLoading ? (
          <div className="py-12 flex justify-center">
            <div className="w-8 h-8 border-2 border-orange-500/20 border-t-orange-500 rounded-full animate-spin"></div>
          </div>
        ) : (
          <>
            <div className="flex flex-col md:flex-row gap-8">
              {/* Avatar Upload */}
              <div className="flex flex-col items-center gap-4">
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  ref={fileInputRef}
                  onChange={handleAvatarUpload}
                />
                <button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isUploadingAvatar}
                  className="w-24 h-24 rounded-full bg-gradient-to-br from-orange-500/20 to-yellow-500/20 border border-orange-500/30 flex items-center justify-center relative group overflow-hidden transition-all duration-300 hover:border-orange-500/60 focus:outline-none"
                >
                  {isUploadingAvatar ? (
                    <div className="w-6 h-6 border-2 border-orange-500/30 border-t-orange-500 rounded-full animate-spin"></div>
                  ) : avatarUrl ? (
                    <img src={avatarUrl} alt="Avatar" className="w-full h-full object-cover" />
                  ) : (
                    <span className="text-3xl text-amber-100 font-semibold group-hover:opacity-0 transition-opacity duration-300">
                      {fullName ? fullName.charAt(0).toUpperCase() : 'U'}
                    </span>
                  )}
                  {!isUploadingAvatar && (
                    <div className="absolute inset-0 bg-black/60 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <span className="text-xs text-white font-medium">Upload</span>
                    </div>
                  )}
                </button>
              </div>

              {/* Form Fields */}
              <div className="flex-1 space-y-5">
                <div>
                  <label className="block text-sm font-medium text-zinc-400 mb-1.5 flex items-center gap-2">
                    <SafeIcon icon={FiUser} /> Full Name
                  </label>
                  <input
                    type="text"
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                    placeholder="e.g. Jane Doe"
                    className={inputStyles}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-zinc-400 mb-1.5 flex items-center gap-2">
                    <SafeIcon icon={FiMail} /> Email Address
                  </label>
                  <div className="flex gap-3">
                    <input
                      type="email"
                      value={user?.email || ''}
                      disabled
                      className={`${inputStyles} opacity-50 cursor-not-allowed`}
                    />
                    {isEmailVerified ? (
                      <Button variant="secondary" disabled className="flex-shrink-0 whitespace-nowrap hidden sm:flex !opacity-50 !cursor-not-allowed border-green-500/20 text-green-400 hover:border-green-500/20 hover:bg-zinc-900/80">
                        <SafeIcon icon={FiCheckCircle} /> Verified
                      </Button>
                    ) : (
                      <Button variant="secondary" className="flex-shrink-0 whitespace-nowrap hidden sm:flex">
                        Verify
                      </Button>
                    )}
                  </div>
                </div>
                <div className="pt-2">
                  <Button variant="secondary" className="w-full sm:w-auto">
                    <SafeIcon icon={FiLock} /> Update Password
                  </Button>
                </div>
              </div>
            </div>

            {/* Referral Section */}
            <div className="mt-8 pt-6 border-t border-orange-500/10">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-xl bg-orange-500/10 flex items-center justify-center flex-shrink-0 border border-orange-500/20">
                  <SafeIcon icon={FiAward} className="text-orange-400 text-lg" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-1">
                    <h4 className="text-md font-medium text-amber-50">Refer & Earn Premium</h4>
                    {totalReferred > 0 && (
                      <div className="px-2.5 py-1 bg-orange-500/10 text-orange-400 text-xs font-semibold rounded-md border border-orange-500/20 flex items-center gap-1.5 shadow-[0_0_15px_rgba(234,88,12,0.1)]">
                        <SafeIcon icon={FiCheckCircle} /> {totalReferred} Referred
                      </div>
                    )}
                  </div>
                  <p className="text-sm text-zinc-400 mb-3">Each paid user you bring gets you free access to 1-month of MoT Premium.</p>
                  <div className="relative">
                    <input
                      type="text"
                      readOnly
                      value={refLink}
                      className={`${inputStyles} pr-24 bg-zinc-950/80 text-zinc-500 border-zinc-800 cursor-not-allowed`}
                    />
                    <button
                      onClick={handleCopyRef}
                      className="absolute right-2 top-1/2 -translate-y-1/2 px-3 py-1.5 bg-orange-500/20 hover:bg-orange-500/30 text-orange-400 rounded-lg text-xs font-medium transition-colors duration-300 flex items-center gap-1.5"
                    >
                      <SafeIcon icon={copiedRef ? FiCheckCircle : FiCopy} /> {copiedRef ? 'Copied' : 'Copy'}
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Public ID Section */}
            <div className="mt-8 pt-6 border-t border-orange-500/10">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-xl bg-orange-500/10 flex items-center justify-center flex-shrink-0 border border-orange-500/20">
                  <SafeIcon icon={FiGlobe} className="text-orange-400 text-lg" />
                </div>
                <div className="flex-1">
                  <h4 className="text-md font-medium text-amber-50 mb-1">Public ID</h4>
                  <p className="text-sm text-zinc-400 mb-3">This sets the URL for your public "My Page" Wall of Love.</p>
                  <div className="relative flex items-center">
                    <span className="absolute left-4 text-zinc-500 text-sm hidden sm:inline">moment.reviews/p/</span>
                    <input
                      type="text"
                      value={publicId}
                      onChange={(e) => setPublicId(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
                      placeholder="e.g. acmecorp"
                      className={`${inputStyles} sm:pl-[255px]`}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Save Actions */}
            <div className="mt-8 pt-6 border-t border-orange-500/10 flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="w-full sm:w-auto">
                {profileMessage.text && (
                  <span className={`text-sm flex items-center gap-1.5 ${profileMessage.type === 'error' ? 'text-red-400' : 'text-green-400'}`}>
                    <SafeIcon icon={profileMessage.type === 'error' ? FiAlertCircle : FiCheckCircle} />
                    {profileMessage.text}
                  </span>
                )}
              </div>
              <Button
                onClick={handleSaveProfile}
                disabled={profileSaving}
                variant="primary"
                className="w-full sm:w-auto"
              >
                {profileSaving ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          </>
        )}
      </SettingsCard>

      {/* Billing Status Message */}
      <AnimatePresence>
        {billingMessage.text && (
          <motion.div
            initial={{ height: 0, opacity: 0, marginBottom: 0 }}
            animate={{ height: 'auto', opacity: 1, marginBottom: 32 }}
            exit={{ height: 0, opacity: 0, marginBottom: 0 }}
            className="overflow-hidden"
          >
            <div className={`p-4 rounded-2xl border flex items-center justify-between gap-3 ${
              billingMessage.type === 'success' 
                ? 'bg-green-500/10 border-green-500/20 text-green-400 shadow-[0_0_20px_rgba(34,197,94,0.15)]' 
                : 'bg-red-500/10 border-red-500/20 text-red-400 shadow-[0_0_20px_rgba(239,68,68,0.15)]'
            }`}>
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${
                  billingMessage.type === 'success' ? 'bg-green-500/20' : 'bg-red-500/20'
                }`}>
                  <SafeIcon icon={billingMessage.type === 'success' ? FiCheckCircle : FiAlertCircle} className="text-xl" />
                </div>
                <div>
                  <h4 className="text-sm font-bold text-amber-50">
                    {billingMessage.type === 'success' ? 'Payment Successful' : 'Payment Canceled'}
                  </h4>
                  <p className="text-sm opacity-90 mt-0.5">{billingMessage.text}</p>
                </div>
              </div>
              <button 
                onClick={() => setBillingMessage({ type: '', text: '' })}
                className="p-2 hover:bg-white/10 rounded-xl transition-colors shrink-0"
              >
                <SafeIcon icon={FiX} />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Subscription CTA conditional render based on user.is_pro */}
      {user?.is_pro ? (
        <div className="relative overflow-hidden rounded-2xl p-[1px] bg-gradient-to-r from-green-500/50 to-emerald-500/50">
          <div className="absolute inset-0 bg-gradient-to-r from-green-500/20 to-emerald-500/20 blur-xl"></div>
          <div className="relative bg-[#0a0500] rounded-2xl p-8 flex flex-col md:flex-row items-center justify-between gap-6 overflow-hidden">
            <div className="absolute top-0 right-0 w-64 h-64 bg-green-500/10 blur-[80px] pointer-events-none"></div>
            
            <div>
              <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-green-500/10 border border-green-500/20 text-green-400 text-xs font-semibold uppercase tracking-wider mb-3">
                <SafeIcon icon={FiCheckCircle} className="text-green-400" /> Premium Active
              </div>
              <h3 className="text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-amber-100 to-white mb-2">
                {isSubscriptionCancelled ? `Premium active until ${subscriptionPeriodEnd || 'period end'}` : `You're on the ${{ monthly: 'Monthly', yearly: 'Yearly', ltd: 'Lifetime', comp: 'Complimentary Pro' }[subscription?.plan_type] || 'Pro'} Plan`}
              </h3>
              <p className="text-zinc-400 max-w-md">
                Your premium features are unlocked.
                {subscription && isSubscriptionCancelled && subscriptionPeriodEnd && (
                  ` Plan canceled. Your access remains active until ${subscriptionPeriodEnd}.`
                )}
                {subscription && !isSubscriptionCancelled && isSubscriptionActive && subscriptionPeriodEnd && (
                  ` Current billing cycle: auto-renews on ${subscriptionPeriodEnd}.`
                )}
                {subscription && isSubscriptionPastDue && subscriptionPeriodEnd && (
                  ` Payment past due. Please update billing before ${subscriptionPeriodEnd}.`
                )}
                {subscription && isSubscriptionInactive && (
                  ` Your subscription is currently ${subscriptionStatus}.`
                )}
              </p>
            </div>
            
            <Button 
              onClick={handleManageBilling} 
              disabled={isBillingLoading}
              variant="secondary" 
              className="flex-shrink-0 w-full md:w-auto hover:border-green-500/50 transition-colors"
            >
              {isBillingLoading ? 'Redirecting...' : 'Manage Billing'}
            </Button>
          </div>
        </div>
      ) : (
        <div className="relative overflow-hidden rounded-2xl p-[1px] bg-gradient-to-r from-orange-500 to-yellow-400">
          <div className="absolute inset-0 bg-gradient-to-r from-orange-500/20 to-yellow-500/20 blur-xl"></div>
          
          <div className="relative bg-[#0a0500] rounded-2xl p-8 flex flex-col overflow-hidden">
            <div className="absolute top-0 right-0 w-64 h-64 bg-yellow-500/10 blur-[80px] pointer-events-none"></div>
            
            {/* Top Part (Main CTA) */}
            <div className="flex flex-col md:flex-row items-center justify-between gap-6 relative z-10 w-full text-center md:text-left">
              <div className="flex-1">
                <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-yellow-500/10 border border-yellow-500/20 text-yellow-400 text-xs font-semibold uppercase tracking-wider mb-3">
                  <SafeIcon icon={FiStar} className="fill-yellow-400" /> Pro Plan
                </div>
                <h3 className="text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-amber-100 to-white mb-2">Upgrade to Premium</h3>
                <p className="text-zinc-400 max-w-lg mx-auto md:mx-0 leading-relaxed">
                  Unlimited published reviews, every wall layout, unlimited widgets, and your wall without our branding. The kind of trust people remember.
                </p>
              </div>
              
              <div className="flex flex-col items-center gap-2.5 w-full md:w-auto shrink-0">
                <Button 
                  onClick={() => handleCheckout('monthly')} 
                  variant="primary" 
                  className="w-full sm:w-auto shadow-[0_0_30px_rgba(234,88,12,0.3)] !px-8 !py-3.5"
                >
                  Try Premium – $21/mo
                </Button>
                <button
                  onClick={() => handleCheckout('yearly')}
                  className="text-[11px] font-medium text-zinc-500 hover:text-amber-100 transition-colors"
                >
                  or <span className="font-semibold text-orange-400">$210/yr</span> — two months free
                </button>
              </div>
            </div>

            {/* Toggle Button */}
            <div className="mt-8 pt-6 border-t border-white/5 flex justify-center relative z-10">
              <motion.button 
                onClick={() => setShowFeatures(!showFeatures)}
                animate={{ y: [0, -3, 0] }}
                transition={{ repeat: Infinity, duration: 2.5, ease: "easeInOut" }}
                className="flex items-center gap-2 text-sm text-orange-400/80 hover:text-orange-400 transition-colors font-medium rounded-full px-4 py-1.5 hover:bg-orange-500/10"
              >
                See what Premium unlocks
                <motion.div animate={{ rotate: showFeatures ? 180 : 0 }} transition={{ duration: 0.3 }}>
                  <SafeIcon icon={FiChevronDown} />
                </motion.div>
              </motion.button>
            </div>

            {/* Expanded Table */}
            <AnimatePresence>
              {showFeatures && (
                <motion.div 
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.4, ease: "easeInOut" }}
                  className="overflow-hidden"
                >
                  <div className="pt-6 relative z-10">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {/* Free Forever */}
                      <div className="p-5 rounded-xl bg-zinc-900/40 border border-white/5 flex flex-col">
                        <h4 className="text-amber-50 font-semibold mb-4 flex items-center gap-2">
                          <div className="w-2 h-2 rounded-full bg-zinc-500"></div> Free Forever
                        </h4>
                        <ul className="space-y-3.5 flex-1">
                          <li className="flex items-start gap-2.5 text-sm text-zinc-400"><SafeIcon icon={FiCheck} className="text-green-400 mt-0.5 text-base shrink-0" /> <span className="leading-tight">Sync reviews from every platform</span></li>
                          <li className="flex items-start gap-2.5 text-sm text-zinc-400"><SafeIcon icon={FiCheck} className="text-green-400 mt-0.5 text-base shrink-0" /> <span className="leading-tight">Publish up to 25 reviews</span></li>
                          <li className="flex items-start gap-2.5 text-sm text-zinc-400"><SafeIcon icon={FiCheck} className="text-green-400 mt-0.5 text-base shrink-0" /> <span className="leading-tight">Hosted review wall + Grid & Masonry layouts</span></li>
                          <li className="flex items-start gap-2.5 text-sm text-zinc-400"><SafeIcon icon={FiCheck} className="text-green-400 mt-0.5 text-base shrink-0" /> <span className="leading-tight">1 embeddable widget</span></li>
                          <li className="flex items-start gap-2.5 text-sm text-zinc-600 opacity-60"><SafeIcon icon={FiX} className="mt-0.5 text-base shrink-0" /> <span className="leading-tight">All 11 wall layouts & unlimited widgets</span></li>
                          <li className="flex items-start gap-2.5 text-sm text-zinc-600 opacity-60"><SafeIcon icon={FiX} className="mt-0.5 text-base shrink-0" /> <span className="leading-tight">Remove Moment of Truth branding</span></li>
                        </ul>
                      </div>
                      
                      {/* Moment Pro */}
                      <div className="p-5 rounded-xl bg-gradient-to-b from-orange-500/10 to-transparent border border-orange-500/20 relative overflow-hidden flex flex-col shadow-[inset_0_1px_0_rgba(234,88,12,0.2)]">
                        <div className="absolute -top-6 -right-6 p-4 opacity-10 pointer-events-none"><SafeIcon icon={FiStar} className="text-8xl text-orange-500" /></div>
                        <h4 className="text-amber-50 font-semibold mb-4 flex items-center gap-2 relative z-10">
                          <div className="w-2 h-2 rounded-full bg-orange-500 shadow-[0_0_8px_rgba(234,88,12,0.8)]"></div> Moment Pro
                        </h4>
                        <ul className="space-y-3.5 relative z-10 flex-1">
                          <li className="flex items-start gap-2.5 text-sm text-amber-100/80"><SafeIcon icon={FiCheck} className="text-orange-400 mt-0.5 text-base shrink-0" /> <span className="leading-tight">Everything in Free</span></li>
                          <li className="flex items-start gap-2.5 text-sm text-amber-100/90 font-medium"><SafeIcon icon={FiCheck} className="text-orange-400 mt-0.5 text-base shrink-0" /> <span className="leading-tight">Unlimited published reviews</span></li>
                          <li className="flex items-start gap-2.5 text-sm text-amber-100/90 font-medium"><SafeIcon icon={FiCheck} className="text-orange-400 mt-0.5 text-base shrink-0" /> <span className="leading-tight">All 11 wall layouts</span></li>
                          <li className="flex items-start gap-2.5 text-sm text-amber-100/90 font-medium"><SafeIcon icon={FiCheck} className="text-orange-400 mt-0.5 text-base shrink-0" /> <span className="leading-tight">Unlimited widgets</span></li>
                          <li className="flex items-start gap-2.5 text-sm text-amber-100/90 font-medium"><SafeIcon icon={FiCheck} className="text-orange-400 mt-0.5 text-base shrink-0" /> <span className="leading-tight">No Moment of Truth branding</span></li>
                          <li className="flex items-start gap-2.5 text-sm text-amber-100/80"><SafeIcon icon={FiCheck} className="text-orange-400 mt-0.5 text-base shrink-0" /> <span className="leading-tight">Priority support</span></li>
                          <li className="flex items-start gap-2.5 text-sm text-amber-100/80"><SafeIcon icon={FiCheck} className="text-orange-400 mt-0.5 text-base shrink-0" /> <span className="leading-tight">Custom domain, auto-sync & analytics — rolling out</span></li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        {/* Feedback */}
        <SettingsCard
          title="Feedback"
          description="Help us improve Moment of Truth."
        >
          <div className="space-y-4">
            <div className="flex gap-2">
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  onMouseEnter={() => setHoverRating(star)}
                  onMouseLeave={() => setHoverRating(0)}
                  onClick={() => setRating(star)}
                  className="p-1 transition-transform duration-300 hover:scale-110"
                >
                  <SafeIcon 
                    icon={FiStar} 
                    className={`text-2xl transition-colors duration-300 ${(hoverRating || rating) >= star ? 'text-yellow-400 fill-yellow-400' : 'text-zinc-700'}`}
                  />
                </button>
              ))}
            </div>
            <textarea
              placeholder="What do you love? What could be better?"
              rows={3}
              className={`${inputStyles} resize-none`}
              value={feedback}
              onChange={(e) => setFeedback(e.target.value)}
            />
            <Button 
              onClick={handleFeedbackSubmit} 
              disabled={isSubmittingFeedback || (!rating && !feedback)}
              className="w-full"
            >
              <SafeIcon icon={FiSend} /> {isSubmittingFeedback ? 'Sending...' : 'Submit Feedback'}
            </Button>
          </div>
        </SettingsCard>

        {/* Support */}
        <SettingsCard
          title="Support"
          description="Need help getting set up? We're here for you."
        >
          <div className="h-full flex flex-col items-start justify-between">
            <div>
              <div className="w-12 h-12 rounded-full bg-zinc-900 border border-orange-500/20 flex items-center justify-center mb-4 shadow-[0_0_20px_rgba(234,88,12,0.1)]">
                <SafeIcon icon={FiLifeBuoy} className="text-xl text-orange-400" />
              </div>
              <p className="text-zinc-400 text-sm mb-6">
                Our engineering team responds to all inquiries within 24 hours.
              </p>
            </div>
            <a href="mailto:support@momentoftruth.com" className="w-full">
              <Button variant="secondary" className="w-full">
                Contact Developers
              </Button>
            </a>
          </div>
        </SettingsCard>
      </div>
    </motion.div>
  );
};

export default Settings;